import { assertIsBoolean } from './container/container.assertions';
import DefaultSelectListItemRenderer from './default-select-list-item-renderer';
import List from './list';
import type ListItemRenderer from './list-item-renderer';
import SelectEvent from './select-event';
import SelectListEvent from './select-list-event';
import SelectListItemRenderer from './select-list-item-renderer';

/**
 * The SelectList component main purpose, is to show a visual representation of a data set the user can pick from.
 *
 * This data set can be either an Array or an ObservableCollection.
 *
 * The SelectList is headless, so you can define exactly what you want to show per data set item.
 *
 * You use the data property to set the data that will be rendered and the ItemRenderer property as the class factory you want to use as the visual representation of a single item in the data set.
 *
 * When a user selects or de-selects a rendered List item, a SelectListEvent will be dispatched, that you can listen for anywhere from the SelectList to parents at any DOM level.
 *
 * Everything about drawing rows from data — the data property itself, the ItemRenderer pool, scrollToItem, moving the focus with the arrow keys — is {@link List}, which this extends. What is added here is the selection and nothing else.
 */
export default class SelectList<Item> extends List<Item> {
	/**
	 * The autoSelectWhenFocused property makes the selection follow the focus.
	 *
	 * With it set, pressing ArrowUp or ArrowDown selects the ItemRenderer the key press leaves focused, so a keyboard user moves the selection rather than moving a focus ring over it, and a SelectListEvent is dispatched for each step. Without it, the arrow keys only move focus and the user has to press Space or Enter
	 * to select — which is the default, since a list where every arrow press commits a selection is the wrong thing for a list that is only being looked through.
	 *
	 * It also changes how the Tab key reaches the list. With it set, the list is one tab stop and that stop is the selected ItemRenderer, so tabbing in lands on the selection and the arrow keys carry on from there — the alternative, tabbing through every item to reach the one that is selected, makes the arrow keys
	 * pointless. Without it the list leaves the tab order alone entirely, so every ItemRenderer keeps whatever it decided for itself, as it has always been. See `#updateTabStops`.
	 *
	 * @param value True if the arrow keys should select what they focus. Default is false.
	 */
	public set autoSelectWhenFocused(value: boolean) {
		if (this.#autoSelectWhenFocused === value) {
			return;
		}
		assertIsBoolean(value);
		this.#autoSelectWhenFocused = value;
		this.#autoSelectWhenFocusedChanged = true;
		this.invalidateProperties();
	}

	public get autoSelectWhenFocused(): boolean {
		return this.#autoSelectWhenFocused;
	}

	/**
	 * The allowDeselect property says whether the selection may be emptied.
	 *
	 * With it set to false, the list never goes from something selected to nothing selected. Clicking the selected ItemRenderer, or pressing Space or Enter on it, leaves it selected and dispatches nothing, since nothing has changed; and `selectedItem = null` is refused. Picking another ItemRenderer still moves the
	 * selection, which is the point: a list of choices where one is always the answer, such as the shape a window is cut to.
	 *
	 * With allowMultiSelect set as well, the rule is the same one: the last selected ItemRenderer cannot be de-selected, and any other can.
	 *
	 * New data still clears the selection, because the rows it was made on are gone — that is not de-selecting a row but having none to select.
	 *
	 * @param value False if the selection may never be emptied. Default is true.
	 */
	public set allowDeselect(value: boolean) {
		assertIsBoolean(value);
		this.#allowDeselect = value;
	}

	public get allowDeselect(): boolean {
		return this.#allowDeselect;
	}

	public set allowMultiSelect(value: boolean) {
		if (this.#allowMultiSelect === value) {
			return;
		}
		this.#allowMultiSelect = value;
		// the list is built again, which is what collapses a multiple selection down to none when
		// the property is turned off with more than one row selected
		this.invalidateData();
	}

	public get allowMultiSelect(): boolean {
		return this.#allowMultiSelect;
	}

	/**
	 * The ItemRenderer property defines the component class definbition you want to visually represent each of the items in the data set.
	 *
	 * If the ItemRenderer is not defined, a fallback DefaultSelectListItemRenderer is provided so you get some confirmation that the SelectList is showing data set items correctly.
	 *
	 * When you create your ItemRenderer, you must extend the SelectListItemRenderer class, so the rows the SelectList hands out can be selected.
	 */
	public override set ItemRenderer(value: new () => SelectListItemRenderer<Item>) {
		super.ItemRenderer = value;
	}

	public override get ItemRenderer(): new () => SelectListItemRenderer<Item> {
		return super.ItemRenderer as new () => SelectListItemRenderer<Item>;
	}

	/**
	 * Selects the Item, or empties the selection with null.
	 *
	 * Null is refused while allowDeselect is false, the same as a click on the selected ItemRenderer is.
	 *
	 * No guard against the value set last, and that is deliberate: a click moves the selection without passing through here, so the value set last can be one the list no longer shows, and guarding against it would ignore the very value that should put it back. Whether anything needs doing is decided when it is
	 * committed, against what is actually selected — see #commitSelectedItem.
	 */
	public set selectedItem(value: Item | null) {
		if (value === null && !this.#allowDeselect) {
			return;
		}
		this.#selectedItem = value;
		this.#selectedItemChanged = true;
		this.invalidateProperties();
	}

	/**
	 * Returns the currently selected Item in the SelectList.
	 *
	 * If allowMultiSelect is true, then the last selected Item is returned.
	 */
	public get selectedItem(): Item | null {
		// we can get the last element in an Array with at(-1)
		const item = this.#selectedMap.keys().toArray().at(-1);
		if (item === undefined) {
			return null;
		}
		return item;
	}

	/**
	 * Returns the currently selectedItems.
	 *
	 * If allowMultiSelect is false, selectedItems will still be an Array but only contain one Item or empty if nothing is selected.
	 */
	public get selectedItems(): Item[] {
		return this.#selectedMap.keys().toArray();
	}

	#allowDeselect: boolean;
	#allowMultiSelect: boolean;
	#autoSelectWhenFocused: boolean;
	#autoSelectWhenFocusedChanged: boolean;
	#selectedItem: Item | null;
	#selectedItemChanged: boolean;
	#selectedMap: Map<Item, SelectListItemRenderer<Item>>;

	public constructor() {
		super();
		this.#allowDeselect = true;
		this.#allowMultiSelect = false;
		this.#autoSelectWhenFocused = false;
		this.#autoSelectWhenFocusedChanged = false;
		this.#selectedItem = null;
		this.#selectedItemChanged = false;
		this.#selectedMap = new Map();
		this.ItemRenderer = DefaultSelectListItemRenderer;
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#selectedItemChanged) {
			this.#commitSelectedItem();
		}
		// last, because the data pass above creates the ItemRenderers and the one above that moves
		// the selection between them, and which one is the tab stop follows from both
		if (this.#autoSelectWhenFocusedChanged) {
			this.#commitAutoSelectWhenFocused();
		}
	}

	protected override dataCommitted(): void {
		super.dataCommitted();
		// the rows are new, so nothing carries a selection any more
		this.#selectedMap.clear();
		this.#updateTabStops();
		this.#dispatchSelectionChangedEvent();
	}

	/** Selects whatever the arrow keys just landed on, when the list has been asked to keep the selection with the focus. */
	protected override focusMovedByKeyboard(): void {
		super.focusMovedByKeyboard();
		if (this.#autoSelectWhenFocused) {
			this.#selectFocusedItemRenderer();
		}
	}

	protected override initialize(): void {
		super.initialize();
		this.addEventListener(SelectEvent.SELECTED_CHANGED, this.#itemRendererSelectedChanged as EventListener);
	}

	/** The selection is kept by Item, so an Item that is replaced is still the selection, and anything holding the Item it was made with has to hear that it is now another one. */
	protected override itemReplaced(current: Item, replacement: Item, itemRenderer: ListItemRenderer<Item>): void {
		super.itemReplaced(current, replacement, itemRenderer);
		if (this.#selectedMap.has(current)) {
			assertIsSelectListItemRenderer<Item>(itemRenderer);
			this.#selectedMap.delete(current);
			this.#selectedMap.set(replacement, itemRenderer);
			this.#dispatchSelectionChangedEvent();
		}
	}

	protected override recycleItemRenderer(itemRenderer: ListItemRenderer<Item>): void {
		super.recycleItemRenderer(itemRenderer);
		assertIsSelectListItemRenderer<Item>(itemRenderer);
		itemRenderer.selected = false;
	}

	/** A row that joined is not selected, but which row is the tab stop may have to be said again now that there is one more. */
	protected override itemInserted(item: Item, itemRenderer: ListItemRenderer<Item>): void {
		super.itemInserted(item, itemRenderer);
		this.#updateTabStops();
	}

	/** An Item that left takes its selection with it, and anything holding the selection has to hear that it is gone — the way a full pass says so for every row at once. */
	protected override itemRemoved(item: Item, itemRenderer: ListItemRenderer<Item>): void {
		super.itemRemoved(item, itemRenderer);
		if (this.#selectedMap.delete(item)) {
			this.#dispatchSelectionChangedEvent();
		}
		this.#updateTabStops();
	}

	#commitAutoSelectWhenFocused(): void {
		this.#autoSelectWhenFocusedChanged = false;
		this.#updateTabStops();
	}

	#commitSelectedItem(): void {
		this.#selectedItemChanged = false;
		// measured against what is selected rather than against what was set before, for the reason
		// given on the setter. Already the whole selection, it is left alone and nothing is said
		if (this.#isTheWholeSelection(this.#selectedItem)) {
			return;
		}
		this.#deSelectCurrentlySelectedItemRenderers();
		if (this.#selectedItem) {
			const itemRenderer = this.itemRendererFor(this.#selectedItem);
			if (itemRenderer) {
				// we can invoke the synchronous click() method that will
				// select the itemRenderer and dispatch the SelectEvent.SELECTED_CHANGED
				// this ensures that everything will work correctly and exactly as if
				// the user clicked the itemRenderer manually
				itemRenderer.click();
			}
		} else {
			// if the #selectedItem is null,
			// we still need to notify listeners,
			// since the selectedItem could have been not null
			this.#dispatchSelectionChangedEvent();
		}
	}

	/** Whether the selection is that Item and nothing else, or nothing at all for null. */
	#isTheWholeSelection(item: Item | null): boolean {
		const selectedItems = this.selectedItems;
		if (item === null) {
			return selectedItems.length === 0;
		}
		return selectedItems.length === 1 && selectedItems[0] === item;
	}

	#deSelectCurrentlySelectedItemRenderers(): void {
		for (const renderer of this.#selectedMap.values()) {
			renderer.selected = false;
		}
		this.#selectedMap.clear();
	}

	#dispatchSelectionChangedEvent(): void {
		this.dispatchEvent(new SelectListEvent<Item | null>(this.selectedItem, this.selectedItems));
	}

	#itemRendererSelectedChanged(event: SelectEvent<Item>): void {
		// we stop the event from bubbling further up the DOM since Lists can have Lists as children
		// which will result in catching an Event from it's grandchildren's Lists and result in selection bugs
		event.stopImmediatePropagation();
		assertIsSelectListItemRenderer<Item>(event.target);
		const itemRenderer = event.target;
		if (this.#isRefusedDeselect(itemRenderer)) {
			// put back rather than let through: the renderer has already turned itself off, and it
			// commits later, so it never shows as de-selected. Nothing is dispatched, since the
			// selection is what it was
			itemRenderer.selected = true;
			return;
		}
		if (itemRenderer.selected) {
			if (!this.#allowMultiSelect) {
				this.#deSelectCurrentlySelectedItemRenderers();
			}
			if (itemRenderer.item) {
				this.#selectedMap.set(itemRenderer.item, itemRenderer);
			}
		} else {
			if (itemRenderer.item) {
				this.#selectedMap.delete(itemRenderer.item);
			}
		}
		this.#updateTabStops();
		this.#dispatchSelectionChangedEvent();
	}

	/** Whether the ItemRenderer has just turned itself off, and turning it off would leave nothing selected while that is not allowed. */
	#isRefusedDeselect(itemRenderer: SelectListItemRenderer<Item>): boolean {
		if (this.#allowDeselect || itemRenderer.selected || itemRenderer.item === null) {
			return false;
		}
		return this.#selectedMap.size === 1 && this.#selectedMap.has(itemRenderer.item);
	}

	/**
	 * Selects the ItemRenderer that has the focus, unless it already has the selection.
	 *
	 * Selected by click(), because that is the only path that dispatches the SelectEvent the list keeps its own selection from — see the comment in #commitSelectedItem. The click toggles, which is why an ItemRenderer that is already selected is left alone: clicking it would de-select it, and the arrow keys are here to
	 * select.
	 *
	 * That also covers the ends of the list, where the arrow key moved no focus: the ItemRenderer that stays focused is selected if it was not, and left alone if it was.
	 */
	#selectFocusedItemRenderer(): void {
		// asserted rather than checked: the keyboard listener only exists while an ItemRenderer has
		// the focus, so a null here is a fault and not a case
		assertIsSelectListItemRenderer<Item>(this.currentlyFocusedItemRenderer);
		if (this.currentlyFocusedItemRenderer.selected) {
			return;
		}
		this.currentlyFocusedItemRenderer.click();
	}

	/** The ItemRenderer the Tab key should reach: the selected one, or the first. */
	#tabStopItemRenderer(): null | SelectListItemRenderer<Item> {
		const selectedItem = this.selectedItem;
		if (selectedItem !== null) {
			const selected = this.#selectedMap.get(selectedItem);
			if (selected !== undefined) {
				return selected;
			}
		}
		const first = this.children.item(0);
		if (first === null) {
			return null;
		}
		assertIsSelectListItemRenderer<Item>(first);
		return first;
	}

	/**
	 * Hands out the one tab stop, or gives it back to every ItemRenderer.
	 *
	 * With autoSelectWhenFocused set, the list is a single tab stop: the selected ItemRenderer, or the first one when nothing is selected, since a list no Tab can reach is a list a keyboard cannot use at all. Every other ItemRenderer keeps its focus by pointer and by focus() and is passed over by Tab — which is what
	 * the tabbable property is for, and its own documentation names arrow keys into a list as the case it was written for.
	 *
	 * Without the flag this does nothing at all, and that is the important half. An ItemRenderer may have taken itself out of the tab order for its own reasons — SearchModelItemRenderer does, because its list sits in a popover between two fields and every model found would otherwise land in the way of tabbing on — and
	 * the list cannot tell a renderer that chose to be tabbable from one that simply never said. So it grants nothing it did not take away: a list only manages the tab order once it has been asked to.
	 *
	 * The consequence is that turning the flag off again leaves the tab stops where they were. That is deliberate: the alternative is handing tabbability back to a renderer that never wanted it, and the property is set once when a list is built rather than toggled while it is in use.
	 */
	#updateTabStops(): void {
		if (this.#autoSelectWhenFocused === false) {
			return;
		}
		const tabStop = this.#tabStopItemRenderer();
		for (const itemRenderer of this.itemRenderers()) {
			itemRenderer.tabbable = itemRenderer === tabStop;
		}
	}
}
customElements.define('ac-select-list', SelectList);

function assertIsSelectListItemRenderer<Item>(value: unknown): asserts value is SelectListItemRenderer<Item> {
	if (value instanceof SelectListItemRenderer) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be an instance of ListItemRenderer`);
}
